import React, { useState, useRef, useEffect } from "react";
import {
  Search,
  Calendar,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { setMerchantId } from "../../features/global/globalSlice";

// --- Phần CustomMonthPicker giữ nguyên như bạn đã viết ---
const CustomMonthPicker = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const containerRef = useRef(null);

  useEffect(() => {
    if (value) {
      const [y] = value.split("-");
      setViewYear(parseInt(y));
    }
  }, [value, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const months = [
    "Tháng 1",
    "Tháng 2",
    "Tháng 3",
    "Tháng 4",
    "Tháng 5",
    "Tháng 6",
    "Tháng 7",
    "Tháng 8",
    "Tháng 9",
    "Tháng 10",
    "Tháng 11",
    "Tháng 12",
  ];

  const handleSelectMonth = (monthIndex) => {
    const monthStr = (monthIndex + 1).toString().padStart(2, "0");
    onChange(`${viewYear}-${monthStr}`);
    setIsOpen(false);
  };

  const displayValue = value
    ? `Tháng ${parseInt(value.split("-")[1])}, ${value.split("-")[0]}`
    : "Chọn tháng...";

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full pl-11 pr-10 py-3 cursor-pointer
          bg-slate-50/50 border rounded-2xl transition-all select-none
          flex items-center text-sm font-semibold
          ${isOpen ? "border-indigo-400 ring-4 ring-indigo-500/5 bg-white" : "border-slate-200 hover:border-indigo-300"}
          ${value ? "text-slate-700" : "text-slate-400"}
        `}
      >
        {displayValue}
        <ChevronDown
          size={16}
          className={`absolute right-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-full min-w-[280px] bg-white rounded-2xl shadow-xl border border-slate-100 p-4 animate-in fade-in zoom-in-95 duration-200 origin-top-left">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setViewYear(viewYear - 1);
              }}
              className="p-1 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm font-black text-slate-700 select-none">
              Năm {viewYear}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setViewYear(viewYear + 1);
              }}
              className="p-1 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {months.map((m, idx) => {
              const isSelected =
                value ===
                `${viewYear}-${(idx + 1).toString().padStart(2, "0")}`;
              const isCurrentMonth =
                new Date().getMonth() === idx &&
                new Date().getFullYear() === viewYear;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSelectMonth(idx)}
                  className={`
                    py-2.5 px-1 rounded-xl text-xs font-bold transition-all
                    ${isSelected ? "bg-indigo-600 text-white shadow-lg shadow-indigo-200" : isCurrentMonth ? "bg-indigo-50 text-indigo-600 border border-indigo-100" : "text-slate-600 hover:bg-slate-50 hover:text-indigo-600"}
                  `}
                >
                  {m.replace("Tháng ", "Thg ")}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
// --- Hết phần CustomMonthPicker ---

const SearchForm = ({
  searchCode,
  setSearchCode,
  handleSearch,
  isLoading,
  selectedMonth,
  setSelectedMonth,
}) => {
  const dispatch = useDispatch();

  const onFormSubmit = (e) => {
    e.preventDefault();
    const nextMerchantId = searchCode.trim();

    if (nextMerchantId) {
      setSearchCode(nextMerchantId);
      dispatch(setMerchantId(nextMerchantId));
    }

    handleSearch(e, nextMerchantId);
  };

  return (
    <form
      onSubmit={onFormSubmit}
      className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-wrap lg:flex-nowrap items-end gap-4 mb-8"
    >
      <div className="flex-1 min-w-[240px] space-y-1.5">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
          Mã định danh cửa hàng
        </label>
        <div className="relative group">
          <Search
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors"
            size={18}
          />
          <input
            type="text"
            placeholder="Nhập mã (VD: TOMOTEK)"
            className="w-full pl-11 pr-4 py-3 bg-slate-50/50 border border-slate-200 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-400 focus:bg-white transition-all text-sm font-semibold text-slate-700"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            required
          />
        </div>
      </div>
      <div className="w-full lg:w-64 space-y-2">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
          Tháng báo cáo
        </label>
        <div className="relative group">
          <div className="absolute left-3.5 top-3 z-10 pointer-events-none">
            <Calendar
              className="text-slate-300 group-focus-within:text-indigo-500 transition-colors"
              size={18}
            />
          </div>
          <CustomMonthPicker
            value={selectedMonth}
            onChange={setSelectedMonth}
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={isLoading}
        className="w-full lg:w-auto px-10 py-3 bg-indigo-600 text-white rounded-2xl font-black text-sm hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-indigo-100 active:scale-95"
      >
        {isLoading ? (
          <Loader2 className="animate-spin" size={18} />
        ) : (
          "Truy xuất dữ liệu"
        )}
      </button>
    </form>
  );
};

export default SearchForm;
